// ============================================================
// CANVAS & LAYOUT  (Card Tactician — M4: 16:9 橫向 PC + 45° iso)
// ============================================================
//
// 從三消的直向手機版面改成 PC 橫向：
//   - 內部固定邏輯解析度 1280×720 (16:9)，所有繪圖 / 命中測試都用這組
//     邏輯座標；canvas 以 letterbox 等比縮放充滿視窗（多餘處留黑邊）。
//   - 戰場改 45° 等角投影（cellToScreen / screenToCell），ITB 風格立體格。
//   - 舊三消 zone 常數（CB/CM/ZBY/ZHY/ZMY/OXB/OYB/OXM/OYM）仍保留定義，
//     讓尚未改寫的三消畫面（M5 才刪）能編譯；DEPLOY 仍用 OXB/OYB/CB 的
//     正交 8×8 grid（橫向置中），戰鬥畫面 (_battlefield) 才走 iso。
//
// 座標換算（給 Input 用）：螢幕事件 px → 邏輯座標，用 canvas
// getBoundingClientRect 的縮放比；helper `eventToLogical` 封裝。

import { BATTLE_COLS, BATTLE_ROWS, M3_COLS, M3_ROWS } from './constants.ts';

export const canvas = document.getElementById('gameCanvas') as HTMLCanvasElement;
export const ctx = canvas.getContext('2d')!;

/** 邏輯內部解析度（16:9）。所有繪圖 / 命中座標都以此為基準。 */
export const VW = 1280;
export const VH = 720;

/** 對外沿用 W/H 名稱（現在是常數邏輯尺寸，不再隨視窗變動）。 */
export const W = VW;
export const H = VH;

// ── 橫向版面區（邏輯座標；ITB 風格）────────────────────────
/** 頂列 HUD 高度（回合 / 基地 HP / 確認鈕）。 */
export const TOP_BAR_H = 54;
/** 底部手牌列高度。 */
export const HAND_H = 150;
/** 手牌列上方的 3-tick 時間軸高度。 */
export const TIMELINE_H = 40;
/** 左欄（牌庫 / 棄牌）寬度。 */
export const LEFT_RAIL_W = 150;

/**
 * DEPLOY 底部面板頂緣。DEPLOY 沒有手牌列，可用比 SCHED 的 ZMY(=VH-HAND_H) 更高的
 * 起點容納「調色盤 + 牌組選擇 + 開戰 + 存讀檔 + 返回」諸列（原本錨在 ZMY=570 會爆版切到按鈕）。
 * ⚠️ Renderer._deployPanel 與 Input._getDeployButtons / 佈陣路由都必須用這個值（命中矩形同源）。
 */
export const DEPLOY_PANEL_TOP = VH - 260; // = 460
/** 右欄（選中卡詳情 / 目標資訊）寬度。 */
export const RIGHT_PANEL_W = 280;

/** 戰場可用矩形（iso 菱形置中於此）。 */
export const FIELD_X = LEFT_RAIL_W;
export const FIELD_Y = TOP_BAR_H;
export const FIELD_W = VW - LEFT_RAIL_W - RIGHT_PANEL_W;
export const FIELD_H = VH - TOP_BAR_H - HAND_H - TIMELINE_H;

// ── iso 投影參數 ───────────────────────────────────────────
/** 等角菱形 tile 寬 / 高（2:1）。 */
export const ISO_TW = 88;
export const ISO_TH = 44;
/** ITB 立體格的側面厚度（px）。 */
export const ISO_DEPTH = 12;
/** iso 原點（cell(0,0) 的菱形中心）。在 resize 時依 FIELD_* 置中算出。 */
export let ISO_OX = 0;
export let ISO_OY = 0;

// ── letterbox 縮放資訊（Input 用）──────────────────────────
export let viewScale = 1;

// ── 舊三消 zone 常數（保留定義；M5 刪三消時一併清）──────────
export let CB = 0;   // battle/deploy 正交格大小（DEPLOY 仍用）
export let CM = 0;   // match-3 格大小（三消遺留）
export let ZBY = 0;
export let ZHY = 0;
export let ZMY = 0;
export let OXB = 0;  // 正交 8×8 grid offset（DEPLOY 用）
export let OYB = 0;
export let OXM = 0;
export let OYM = 0;

/** cell(cx,cy) → 該菱形「頂面中心」的邏輯螢幕座標。 */
export function cellToScreen(cx: number, cy: number): { x: number; y: number } {
  return {
    x: ISO_OX + (cx - cy) * (ISO_TW / 2),
    y: ISO_OY + (cx + cy) * (ISO_TH / 2),
  };
}

/**
 * 邏輯螢幕座標 → cell(cx,cy)（未夾邊界；呼叫端自行檢查 0..N-1）。
 * 用 `round`（非 floor）才能與 `cellToScreen`（回傳格中心）對齊——floor 會
 * 把整個菱形往「頂角在格中心」偏半格，導致點到單位常落到斜上一格。
 */
export function screenToCell(sx: number, sy: number): { cx: number; cy: number } {
  const a = (sx - ISO_OX) / (ISO_TW / 2); // = cx - cy
  const b = (sy - ISO_OY) / (ISO_TH / 2); // = cx + cy
  return { cx: Math.round((a + b) / 2), cy: Math.round((b - a) / 2) };
}

/** 螢幕事件 (clientX/Y) → 邏輯座標（吃 letterbox 縮放 + 置中位移）。 */
export function eventToLogical(clientX: number, clientY: number): { x: number; y: number } {
  const rect = canvas.getBoundingClientRect();
  const sx = rect.width > 0 ? VW / rect.width : 1;
  const sy = rect.height > 0 ? VH / rect.height : 1;
  return { x: (clientX - rect.left) * sx, y: (clientY - rect.top) * sy };
}

export function resize(): void {
  const dpr = window.devicePixelRatio || 1;
  const winW = Math.max(320, window.innerWidth);
  const winH = Math.max(240, window.innerHeight);

  // letterbox：等比縮放充滿視窗，置中，多餘留黑邊。
  viewScale = Math.min(winW / VW, winH / VH);
  const dispW = Math.round(VW * viewScale);
  const dispH = Math.round(VH * viewScale);

  // backing store = 邏輯解析度 × dpr（清晰）；CSS 顯示尺寸 = letterbox 後尺寸。
  canvas.width = Math.round(VW * dpr);
  canvas.height = Math.round(VH * dpr);
  canvas.style.width = dispW + 'px';
  canvas.style.height = dispH + 'px';
  canvas.style.position = 'absolute';
  canvas.style.left = Math.round((winW - dispW) / 2) + 'px';
  canvas.style.top = Math.round((winH - dispH) / 2) + 'px';
  // 邏輯座標系：1 單位 = 1 邏輯 px（dpr 由 backing store 吸收）。
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

  // iso 原點：把 8×8 菱形置中於戰場矩形。
  // 菱形寬 = (cols-1+rows-1)*TW/2 + TW；高 = (cols-1+rows-1)*TH/2 + TH。
  const span = (BATTLE_COLS - 1 + BATTLE_ROWS - 1);
  const diamondH = span * (ISO_TH / 2) + ISO_TH + ISO_DEPTH;
  // 水平：菱形左右對稱於 ISO_OX（cell(0,0) 在菱形頂端、非左緣）→ 置中 = 場地中線。
  ISO_OX = FIELD_X + FIELD_W / 2;
  // 垂直：cell(0,0) 頂角在 ISO_OY-TH/2；把整個菱形高置中於場地矩形。
  ISO_OY = FIELD_Y + (FIELD_H - diamondH) / 2 + ISO_TH / 2;

  // ── 舊三消 / DEPLOY 正交 grid 常數（橫向置中的 8×8）──────────
  // DEPLOY 仍走正交；把它擺在戰場矩形中央。
  CB = Math.max(8, Math.floor(Math.min(FIELD_H / BATTLE_ROWS, FIELD_W / BATTLE_COLS)));
  OXB = Math.floor(FIELD_X + (FIELD_W - CB * BATTLE_COLS) / 2);
  OYB = Math.floor(FIELD_Y + (FIELD_H - CB * BATTLE_ROWS) / 2);
  ZBY = 0;
  ZHY = FIELD_Y + FIELD_H;
  ZMY = VH - HAND_H;
  // match-3 遺留（畫面已不用；給定義避免 NaN）。
  CM = Math.max(8, Math.floor(Math.min((HAND_H - 8) / M3_ROWS, (FIELD_W) / M3_COLS)));
  OXM = Math.floor(FIELD_X + (FIELD_W - CM * M3_COLS) / 2);
  OYM = ZMY + 4;
}

// Initial resize + listen
resize();
window.addEventListener('resize', resize);
